import { z } from 'zod'

export const evaluatedFieldSchema = z.object({
  key: z.string(),
  path: z.string(),
  expected: z.unknown(),
  actual: z.unknown(),
  match: z.boolean(),
})
export type EvaluatedField = z.output<typeof evaluatedFieldSchema>

export const sampleRecordSchema = z.object({
  sampleId: z.string(),
  formatType: z.string().nullable().optional(),
  // 単一サンプル評価では使うが、評価runビューアは結果テーブルの success を使うため省略を許す。
  success: z.boolean().optional(),
  matchedFields: z.number(),
  totalFields: z.number(),
  fieldResults: z.array(evaluatedFieldSchema),
  extraActualCount: z.number().optional(),
  rowCountExpected: z.number().nullable().optional(),
  rowCountActual: z.number().nullable().optional(),
  durationMs: z.number().optional(),
  usage: z
    .object({
      promptTokens: z.number(),
      completionTokens: z.number(),
      totalTokens: z.number(),
      // 記録開始前に保存された評価runにも対応するため省略を許す。
      cachedPromptTokens: z.number().optional(),
    })
    .nullable()
    .optional(),
  costUsd: z.number().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  pipeline: z
    .object({
      classifiedManufacturer: z.string().nullable(),
      classificationCorrect: z.boolean().nullable(),
      fewShotCount: z.number(),
      usedFallbackPrompt: z.boolean(),
      retried: z.boolean(),
      validationIssues: z.array(z.object({ code: z.string(), severity: z.string(), message: z.string() })),
    })
    .nullable()
    .optional(),
  expected: z.unknown(),
  actual: z.unknown(),
})
export type SampleRecord = z.output<typeof sampleRecordSchema>

export function parseSampleRecord(resultJson: unknown): SampleRecord | null {
  const parsed = sampleRecordSchema.safeParse(resultJson)
  return parsed.success ? parsed.data : null
}

export const runSummarySchema = z.object({
  docAccuracy: z.number(),
  docSuccess: z.number(),
  fieldAccuracy: z.number(),
  totalMatchedFields: z.number(),
  totalFields: z.number(),
  rowCountMismatch: z.number().optional(),
  totalCostUsd: z.number().optional(),
  avgDurationMs: z.number().optional(),
  errorCount: z.number().optional(),
  perField: z.record(z.string(), z.object({ matched: z.number(), total: z.number(), accuracy: z.number() })),
  perFormatType: z.record(
    z.string(),
    z.object({
      count: z.number(),
      docSuccess: z.number(),
      matchedFields: z.number(),
      totalFields: z.number(),
      fieldAccuracy: z.number(),
    }),
  ),
})
export type RunSummary = z.output<typeof runSummarySchema>

export function parseRunSummary(summaryJson: unknown): RunSummary | null {
  const parsed = runSummarySchema.safeParse(summaryJson)
  return parsed.success ? parsed.data : null
}

export const runManifestSchema = z.object({
  pipeline: z.string().optional(),
  split: z.string().optional(),
  detail: z.string().optional(),
  effort: z.string().nullable().optional(),
  input: z.string().optional(),
  verifiedOnly: z.boolean().optional(),
  limit: z.number().nullable().optional(),
  gitCommit: z.string().nullable().optional(),
})
export type RunManifest = z.output<typeof runManifestSchema>

export function parseRunManifest(manifestJson: unknown): RunManifest {
  const parsed = runManifestSchema.safeParse(manifestJson)
  return parsed.success ? parsed.data : {}
}
